export const DATE_RANGE_PRESETS = ['7d', '30d', '90d', '12m', 'custom'] as const;
export type DateRangePreset = (typeof DATE_RANGE_PRESETS)[number];

export const ANALYTICS_USER_ROLES = [
  'CARE_SEEKER',
  'FINANCIAL_ADVISOR',
  'SERVICE_PROVIDER',
  'ADMIN',
] as const;
export type AnalyticsUserRole = (typeof ANALYTICS_USER_ROLES)[number];

export const VETTING_STATUS_OPTIONS = ['APPROVED', 'PENDING', 'ALL'] as const;
export type VettingStatusFilter = (typeof VETTING_STATUS_OPTIONS)[number];

export const SORT_ORDER_OPTIONS = ['asc', 'desc'] as const;
export type SortOrder = (typeof SORT_ORDER_OPTIONS)[number];

export type AnalyticsResponseEnvelope<T> = {
  section: string;
  dateRange: DateRangePreset;
  startDate: string;
  endDate: string;
  lastUpdated: string;
  data: T;
};

export type AnalyticsQueryParams = {
  dateRange?: DateRangePreset;
  startDate?: string;
  endDate?: string;
  partnerCompanyId?: string;
  excludeInternal?: boolean;
};

export type StateCategoryBreakdown = {
  categoryId: string;
  categoryName: string;
  count: number;
};

export type CoverageStateData = {
  stateCode: string;
  stateName: string;
  providerCount: number;
  categories: StateCategoryBreakdown[];
};

export type CoverageData = {
  states: CoverageStateData[];
  totalProviders: number;
  filtersApplied: {
    categoryId: string | null;
    specialtyId: string | null;
    vettingStatus: VettingStatusFilter;
  };
};

export type DrillDownProvider = {
  id: string;
  name: string;
  companyName: string;
  categories: string[];
  specialties: string[];
  vettingStatus: string;
  city: string;
};

export type CoverageDrillDownData = {
  stateCode: string;
  providers: DrillDownProvider[];
  totalCount: number;
  nextCursor: string | null;
};

export type SearchByUserType = {
  CARE_SEEKER: number;
  FINANCIAL_ADVISOR: number;
  SERVICE_PROVIDER: number;
};

export type SearchStateData = {
  stateCode: string;
  stateName: string;
  totalSearches: number;
  byUserType: SearchByUserType;
  topCategory: { categoryId: string; categoryName: string } | null;
  zipcodeSearchPercent: number;
};

export type SearchData = {
  states: SearchStateData[];
  totalSearches: number;
  filtersApplied: {
    userTypes: AnalyticsUserRole[];
    serviceCategoryId: string | null;
  };
};

export type EngagementTypeBreakdown = {
  [key: string]: number;
};

export type TrendPoint = {
  date: string;
  count: number;
  byType?: EngagementTypeBreakdown;
};

export type CategoryBreakdown = {
  categoryId: string;
  categoryName: string;
  count: number;
};

export type RegionBreakdown = {
  stateCode: string;
  stateName: string;
  count: number;
};

export type EngagementTypeCount = {
  engagementType: string;
  count: number;
};

export type EngagementData = {
  total: number;
  monthOverMonthGrowth: number;
  previousPeriodTotal: number;
  trend: TrendPoint[];
  byCategory: CategoryBreakdown[];
  byRegion: RegionBreakdown[];
  byEngagementType: EngagementTypeCount[];
  uniqueInitiators: number;
  uniqueTargets: number;
};

export type LoginKpiCard = {
  totalLogins: number;
  uniqueUsers: number;
};

export type LoginTrendPoint = {
  date: string;
  CARE_SEEKER: number;
  FINANCIAL_ADVISOR: number;
  SERVICE_PROVIDER: number;
  ADMIN: number;
};

export type ActiveUsersPeriod = {
  period: string;
  CARE_SEEKER: number;
  FINANCIAL_ADVISOR: number;
  SERVICE_PROVIDER: number;
  ADMIN: number;
};

export type ActivityData = {
  kpiCards: Record<AnalyticsUserRole, LoginKpiCard>;
  trend: LoginTrendPoint[];
  activeUsers: ActiveUsersPeriod[];
  filtersApplied: {
    partnerCompanyId: string | null;
    excludeInternal: boolean;
  };
};

export type FAClientUser = {
  clientUserId: string;
  clientName: string;
  clientEmail: string;
  lastLogin: string;
  isActive: boolean;
};

export type FAAdvisorRow = {
  faUserId: string;
  faName: string;
  partnerFirm: string;
  partnerCompanyId: string;
  totalClients: number;
  activeClients: number;
  faLastLogin: string;
  totalReferrals: number;
  pendingInvitations: number;
  clients: FAClientUser[];
};

export type FAClientsData = {
  advisors: FAAdvisorRow[];
  totalCount: number;
  nextCursor: string | null;
  containsPII: boolean;
};

export type SPClientUser = {
  clientUserId: string;
  clientName: string;
  clientEmail: string;
  lastEngagement: string;
  isActive: boolean;
};

export type SPProviderRow = {
  spUserId: string;
  spName: string;
  companyName: string;
  serviceCategory: string;
  totalClients: number;
  activeClients: number;
  lastEngagementDate: string;
  totalEngagements: number;
  totalReferrals: number;
  clients: SPClientUser[];
};

export type SPClientsData = {
  providers: SPProviderRow[];
  totalCount: number;
  nextCursor: string | null;
  containsPII: boolean;
};

export type RefreshStatus = 'SUCCESS' | 'FAILED' | 'IN_PROGRESS';

export type RefreshViewStatus = {
  name: string;
  lastRefreshedAt: string;
  status: RefreshStatus;
  durationMs: number | null;
};

export type RefreshStatusResponse = {
  views: RefreshViewStatus[];
  oldestRefresh: string;
  isStale: boolean;
  isRefreshing: boolean;
  cooldownRemainingMs: number;
};

export type RefreshTriggerResponse = {
  status: 'REFRESHING' | 'ALREADY_IN_PROGRESS' | 'COOLDOWN';
  message: string;
  estimatedDurationMs?: number;
  cooldownRemainingMs?: number;
};

export type PartnerCompany = {
  id: string;
  name: string;
  isInternal: boolean;
};

export type PartnerCompaniesResponse = {
  companies: PartnerCompany[];
};

export type ExportSection =
  | 'coverage'
  | 'search'
  | 'engagements'
  | 'logins'
  | 'fa-clients'
  | 'sp-clients'
  | 'financial-partners';

// ---------------------------------------------------------------------------
// Financial Partners (Feature 085)
// ---------------------------------------------------------------------------

export type FpFiltersApplied = {
  partnerCompanyId: string | null;
  excludeInternal: boolean;
};

export type FpReferralValue = {
  total: number;
  monthOverMonthGrowth: number;
  providersEngaged: number;
};

export type FpOverviewData = {
  activeAdvisors: number;
  newAdvisorsInRange: number;
  advisorEngagementRate: number;
  advisorEngagementRateMoM: number;
  activeClients: number;
  clientReferrals: number;
  consultationsBooked: number;
  referralValue: FpReferralValue;
  filtersApplied: FpFiltersApplied;
};

export type FpActionItem = {
  key: string;
  label: string;
  count: number;
  isNew: boolean;
  firmScoped: boolean;
};

export type FpActionsData = {
  actions: FpActionItem[];
  filtersApplied: FpFiltersApplied;
};

export type FpServiceSlice = {
  categoryName: string;
  count: number;
  percentage: number;
};

export type FpReferralsByServiceData = {
  categories: FpServiceSlice[];
  total: number;
  filtersApplied: FpFiltersApplied;
};

// FP Analytics Phase 2 — Top Searches by Category, Client Actions, Engagement Rate trend

export type FpTopSearchesData = {
  categories: FpServiceSlice[];
  total: number;
  firmScoped: boolean;
  filtersApplied: FpFiltersApplied;
};

export type FpClientActionsData = {
  actions: FpServiceSlice[];
  total: number;
  firmScoped: boolean;
  filtersApplied: FpFiltersApplied;
};

export type FpRatePoint = {
  date: string;
  rate: number;
  activeAdvisors: number;
};

export type FpEngagementRateTrendData = {
  points: FpRatePoint[];
  totalAdvisors: number;
  bucket: string;
  firmScoped: boolean;
  filtersApplied: FpFiltersApplied;
};

// FP Analytics Phase 2 (batch 2) — Top Advisors, Retention, Trending, Search Volume

export type FpAdvisorLeaderRow = {
  advisorName: string;
  firmName: string | null;
  totalActions: number;
  referrals: number;
  clientsEngaged: number;
};

export type FpTopAdvisorsData = {
  advisors: FpAdvisorLeaderRow[];
  firmScoped: boolean;
  filtersApplied: FpFiltersApplied;
};

export type FpCountPoint = {
  date: string;
  count: number;
};

export type FpAdvisorRetentionData = {
  points: FpCountPoint[];
  firmScoped: boolean;
  filtersApplied: FpFiltersApplied;
};

export type FpCategorySeries = {
  categoryName: string;
  counts: number[];
};

export type FpTrendingCategoriesData = {
  dates: string[];
  series: FpCategorySeries[];
  bucket: string;
  firmScoped: boolean;
  filtersApplied: FpFiltersApplied;
};

export type FpSearchVolumeData = {
  categories: string[];
  states: string[];
  matrix: number[][];
  max: number;
  firmScoped: boolean;
  filtersApplied: FpFiltersApplied;
};

// FP Analytics — Geographic tiles (Regional Distribution, Emerging Regions, Unmet Demand)

export type FpEngagementSeries = {
  engagementType: string;
  counts: number[];
};

export type FpRegionalDistributionData = {
  states: string[];
  series: FpEngagementSeries[];
  firmScoped: boolean;
  filtersApplied: FpFiltersApplied;
};

export type FpEmergingRegionRow = {
  stateCode: string;
  currentSearches: number;
  previousSearches: number;
  growthPct: number | null;
  isNew: boolean;
};

export type FpEmergingRegionsData = {
  regions: FpEmergingRegionRow[];
  minVolume: number;
  firmScoped: boolean;
  filtersApplied: FpFiltersApplied;
};

export type FpUnmetDemandRow = {
  stateCode: string;
  zeroResultSearches: number;
  topCategory: string | null;
};

export type FpUnmetDemandData = {
  regions: FpUnmetDemandRow[];
  total: number;
  firmScoped: boolean;
  filtersApplied: FpFiltersApplied;
};

export type CoverageQueryParams = AnalyticsQueryParams & {
  categoryId?: string;
  specialtyId?: string;
  vettingStatus?: VettingStatusFilter;
};

export type CoverageDrillDownParams = CoverageQueryParams & {
  stateCode: string;
  limit?: number;
  cursor?: string;
};

export type SearchQueryParams = AnalyticsQueryParams & {
  userTypes?: string;
  serviceCategoryId?: string;
};

export type ActivityQueryParams = AnalyticsQueryParams;

export type LeaderboardQueryParams = {
  partnerCompanyId?: string;
  excludeInternal?: boolean;
  search?: string;
  sortBy?: string;
  sortOrder?: SortOrder;
  limit?: number;
  cursor?: string;
};

export type FAClientsQueryParams = LeaderboardQueryParams & {
  showPendingInvitations?: boolean;
};

export type SPClientsQueryParams = LeaderboardQueryParams & {
  serviceCategoryId?: string;
  stateCode?: string;
};
